import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Gere l'ajout au panier de la fiche Premium - CleanyStep quand le bundle
 * (cs-bundle-selector, voir product-bundle-selector.js) est sur un palier 2
 * ou 3 tapis : au lieu du produit/variante que le formulaire natif porte
 * (toujours celle du produit ORIGINAL - le formulaire ne sait rien du
 * bundle), ajoute la bonne variante X2/X3 (selon la taille choisie) avec les
 * proprietes visibles "Tapis 1/2/3: <couleur>".
 *
 * BUG TROUVE ET CORRIGE (verifie en reel) : la 1ere version interceptait
 * l'evenement 'submit' en phase de capture sur document puis appelait
 * stopImmediatePropagation() - insuffisant en pratique, confirme par un
 * ajout reel au panier : LES DEUX chemins s'executaient (2 lignes ajoutees -
 * le bundle X3 correct, ET une ligne "Premium - CleanyStep" parasite pour la
 * derniere couleur synchronisee cote natif). Le gestionnaire natif
 * (on:submit="/handleSubmit", product-form.js) n'est manifestement pas un
 * simple ecouteur en phase de bouillonnement sur document contournable par
 * cette seule technique. Corrige a la racine plutot qu'en essayant de
 * gagner une course d'evenements : quand le palier est >= 2, le bouton
 * "Ajouter au panier" passe reellement en type="button" - un formulaire ne
 * peut PAS se soumettre nativement (ni au clic, ni via Entree) sans bouton
 * submit, donc aucun evenement 'submit' n'est jamais emis dans ce cas, et
 * product-form.js n'a tout simplement rien a intercepter. Palier 1 : le
 * bouton retrouve type="submit", comportement natif exactement inchange.
 *
 * Meme precaution pour le bouton de paiement express (Shop Pay/Apple Pay/
 * Google Pay, bloc "accelerated-checkout" de buy-buttons.liquid) : ce
 * bouton, natif et hors de notre controle, checkout directement avec le
 * produit/variante du formulaire (donc le produit ORIGINAL) sans jamais
 * passer par cette logique - masque tant que le palier est >= 2 pour eviter
 * qu'un client ne paie le mauvais produit par ce chemin.
 */
function init() {
  const bundle = document.querySelector('cs-bundle-selector');
  if (!bundle) return;

  const section = bundle.closest('.shopify-section');
  const form = section?.querySelector('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return;

  const submitButton = form.querySelector('button[type="submit"]');
  if (!(submitButton instanceof HTMLButtonElement)) return;

  /** @returns {Element[]} Boutons de paiement express de cette section (Shop Pay/Apple Pay/etc.), quel que soit leur etat de rendu actuel. */
  function expressCheckoutElements() {
    return Array.from(
      section.querySelectorAll('shopify-accelerated-checkout, [id^="accelerated-checkout" i]')
    );
  }

  function syncButtonMode() {
    const { tier } = bundle.getSelection();
    submitButton.type = tier === 1 ? 'submit' : 'button';
    expressCheckoutElements().forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (tier === 1) el.style.removeProperty('display');
      else el.style.setProperty('display', 'none', 'important');
    });
  }

  // Le palier peut changer via le selecteur de palier OU implicitement
  // rester le meme quand seule une couleur de tapis change - un seul
  // ecouteur 'change' sur tout le composant couvre les deux, syncButtonMode
  // lui-meme ne fait rien si le palier n'a pas change.
  bundle.addEventListener('change', syncButtonMode);
  syncButtonMode();

  submitButton.addEventListener('click', (event) => {
    if (submitButton.type !== 'button') return; // palier 1 : formulaire natif, rien a faire ici
    event.preventDefault();
    handleBundleAddToCart(bundle, form, submitButton);
  });
}

/**
 * @param {HTMLElement & {getSelection: () => {tier: 1|2|3, size: string, colors: string[]}, tierData: Record<string, Record<string, {variantId: string, price: number, priceFormatted: string}>>}} bundle
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement} submitButton
 */
function handleBundleAddToCart(bundle, form, submitButton) {
  const selection = bundle.getSelection();
  const tierEntry = bundle.tierData?.[String(selection.tier)]?.[selection.size];
  if (!tierEntry) {
    console.error('[cs-bundle-add-to-cart] Aucune variante trouvee pour', selection);
    return;
  }

  const quantityInput = form.querySelector('input[name="quantity"]');
  const quantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) || 1 : 1;

  /** @type {Record<string, string>} */
  const properties = {};
  selection.colors.forEach((color, index) => {
    properties[`Tapis ${index + 1}`] = color;
  });

  submitButton.disabled = true;

  const addPromise = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: tierEntry.variantId.toString(), quantity }],
      promise: addPromise.promise,
    })
  );

  fetch(
    Theme.routes.cart_add_url,
    fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id: tierEntry.variantId, quantity, properties }],
        sections: cartSectionIds().join(','),
      }),
    })
  )
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        addPromise.reject(new Error(response.message || 'Add to cart failed'));
        form.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Impossible d’ajouter ce bundle au panier.',
            code: 'INVALID',
          })
        );
        return;
      }
      resolveWithFullCart(addPromise, response);
    })
    .catch((error) => {
      console.error('[cs-bundle-add-to-cart]', error);
      addPromise.reject(error);
      form.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Erreur reseau lors de l’ajout au panier',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    })
    .finally(() => {
      submitButton.disabled = false;
    });
}

/** @returns {string[]} Section IDs of every cart-items-component on the page. */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(/** @returns {id is string} */ (id) => Boolean(id));
}

/**
 * Resout la promesse de CartLinesUpdateEvent avec un vrai /cart.json complet
 * - meme raison/meme mecanique que #resolveWithFullCart dans
 * cart-line-color-swap.js (la reponse de /cart/add.js seule n'a pas tous les
 * champs qu'un {cart} complet devrait avoir). Fire-and-forget par design.
 * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} promiseHandle
 * @param {{sections?: unknown, items?: unknown}} addResponse
 */
async function resolveWithFullCart(promiseHandle, addResponse) {
  let cart = null;
  try {
    const fullCart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
    cart = CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart);
  } catch (error) {
    console.warn('[cs-bundle-add-to-cart] full cart fetch/conversion failed, continuing anyway', error);
  }

  promiseHandle.resolve({
    cart,
    detail: {
      sections: addResponse.sections,
      items: addResponse.items,
      source: 'cs-bundle-add-to-cart',
      didError: false,
    },
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
